import { useState } from "react";
import { useQueryParamsHook } from "@/hooks/useQueryParamsHook";
import { ProBadge, ProUpsell, useGoPlanTab, usePagePro } from '../FdSubscription/proPlan';
import moment from "moment";
import { useUserPageContextValueHook } from "@/contexts/UserPageContextProvider";
import { useInflowAnalyticsQuery } from "@/hooks/ReactQuery/InflowAnalyticsReactQuery/InflowAnalyticsReactQuery";
import { FgKpiTiles } from "./fragments/FgKpiTiles";
import { FgTrendChart } from "./fragments/FgTrendChart";
import { FgRankBars } from "./fragments/FgRankBars";
import { FgTopLinks } from "./fragments/FgTopLinks";
import { FgLinkDetail } from "./fragments/FgLinkDetail";
import { FgUtmTable } from "./fragments/FgUtmTable";
import { FgHeatmap, getPeakSlot } from "./fragments/FgHeatmap";
import { DEVICE_LABELS, IN_APP_LABELS, WEEKDAYS, formatNumber, getRange } from "./utils";

const PERIODS = [7, 30, 90];
const FREE_MAX_DAYS = 31; // 서버와 같음: 31일 넘는 기간은 Pro

function Section({ title, description, children, className = '' }) {
    return (
        <section className={`rounded-2xl border border-gray-100 bg-white p-5 shadow-[var(--defaultBoxShadow)] min-[993px]:p-6 ${className}`}>
            <div className="mb-4">
                <h3 className="text-[15px] font-bold">{title}</h3>
                {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
            </div>
            {children}
        </section>
    );
}

// 데이터로 바로 읽히는 한 줄 요약
function getInsights(data) {
    const insights = [];
    const { summary, previous, channels, heatmap, topLinks } = data;

    const topChannel = channels.find(c => c.key !== 'direct' && c.key !== 'others');
    if (topChannel && summary.visits) {
        insights.push(`방문의 ${Math.round((topChannel.visits / summary.visits) * 100)}%가 ${topChannel.label}에서 들어왔어요.`);
    }

    const peak = getPeakSlot(heatmap);
    if (peak) {
        insights.push(`방문이 가장 몰리는 때는 ${WEEKDAYS[peak.dow]}요일 ${peak.hour}시예요.`);
    }

    const topLink = topLinks.find(l => !l.deleted);
    if (topLink) {
        insights.push(`가장 많이 눌린 링크는 '${topLink.title}' (클릭 ${formatNumber(topLink.clicks)})이에요.`);
    }

    if (previous.visits && summary.visits !== previous.visits) {
        const ratio = Math.round(((summary.visits - previous.visits) / previous.visits) * 100);
        insights.push(`방문이 이전 ${data.days}일보다 ${Math.abs(ratio)}% ${ratio > 0 ? '늘었어요' : '줄었어요'}.`);
    }

    return insights;
}

export function FdInflowAnalytics(props) {
    const isPro = usePagePro();
    const goPlan = useGoPlanTab();
    const userPageId = useUserPageContextValueHook()?.userPage?.id;
    const [days, setDays] = useState(30);
    const [selectedLink, setSelectedLink] = useState(null);
    const queryParamsHook = useQueryParamsHook();
    const goChannelLinks = () => queryParamsHook.setSearchParams({ ...queryParamsHook?.params, tabType: 'SHORT_LINK' }, { replace: true });
    const range = getRange(days);
    const query = useInflowAnalyticsQuery({ userPageId, ...range });
    const data = query?.data;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="text-xl font-bold">유입 분석</h2>
                    <p className="mt-0.5 text-xs text-gray-500">
                        {data ? `${data.from.replace(/-/g, '.')} ~ ${data.to.replace(/-/g, '.')} · 한국 시간 기준` : '내 페이지 방문과 링크 클릭을 분석합니다.'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => query.refetch()}
                        disabled={query?.isFetching}
                        aria-label="새로고침"
                        title={query?.dataUpdatedAt ? `${moment(query.dataUpdatedAt).format('HH:mm:ss')} 기준 · 새로고침` : '새로고침'}
                        className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                    >
                        <span className={`inline-block ${query?.isFetching ? 'animate-spin' : ''}`} aria-hidden="true">↻</span>
                        {query?.dataUpdatedAt ? moment(query.dataUpdatedAt).format('HH:mm') : ''}
                    </button>
                    <div className="flex rounded-lg border border-gray-200 bg-white p-0.5" role="group" aria-label="기간">
                        {PERIODS.map(p => (
                            <button
                                key={p}
                                type="button"
                                aria-pressed={days === p}
                                onClick={() => (p > FREE_MAX_DAYS && !isPro ? goPlan() : setDays(p))}
                                title={p > FREE_MAX_DAYS && !isPro ? '슈룩 Pro 에서 볼 수 있어요' : undefined}
                                className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold ${days === p ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}
                            >
                                {p}일
                                {p > FREE_MAX_DAYS && !isPro && <ProBadge />}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {query?.isLoading &&
                <div className="flex flex-col gap-4" aria-busy="true">
                    <div className="grid grid-cols-2 min-[1200px]:grid-cols-4 gap-3">
                        {[0, 1, 2, 3].map(i => <div key={i} className="h-[112px] animate-pulse rounded-2xl bg-gray-100" />)}
                    </div>
                    <div className="h-[340px] animate-pulse rounded-2xl bg-gray-100" />
                </div>
            }

            {query?.isError &&
                <div className="rounded-2xl border border-gray-100 p-8 text-center text-sm text-gray-500">
                    유입 분석을 불러오지 못했습니다.
                    <button type="button" onClick={() => query.refetch()} className="ml-2 font-semibold text-gray-900 underline">다시 시도</button>
                </div>
            }

            {data &&
                <>
                    <FgKpiTiles summary={data.summary} previous={data.previous} days={data.days} />

                    {getInsights(data).length > 0 &&
                        <ul className="flex flex-col gap-1.5 rounded-2xl bg-gray-50 px-5 py-4">
                            {getInsights(data).map(text => (
                                <li key={text} className="flex gap-2 text-[13px] text-gray-700">
                                    <span className="text-[var(--mainColor)]" aria-hidden="true">●</span>{text}
                                </li>
                            ))}
                        </ul>
                    }

                    <Section title="방문·클릭 추이" description="날짜에 마우스를 올리면 그날의 클릭률도 볼 수 있어요.">
                        <FgTrendChart daily={data.daily} />
                    </Section>

                    <div className="grid grid-cols-1 min-[1200px]:grid-cols-2 gap-4">
                        <Section title="유입 채널" description="어디에서 페이지로 들어왔는지 (같은 서비스의 여러 주소는 하나로 묶음)">
                            <FgRankBars
                                total={data.summary.visits}
                                rows={data.channels.map(c => ({ key: c.key, label: c.label, value: c.visits, sub: `유효 방문 ${formatNumber(c.firstVisits)}` }))}
                                emptyText="이 기간에 방문이 없습니다."
                            />
                        </Section>
                        <Section title="많이 눌린 링크" description="클릭률 = 링크 클릭 ÷ 페이지 방문 · 링크를 누르면 상세 분석 (Pro)">
                            <FgTopLinks links={data.topLinks} totalClicks={data.summary.clicks} onSelect={setSelectedLink} />
                        </Section>
                    </div>

                    <Section title="방문 시간대" description="요일·시간별 방문 수. 게시물 올릴 시간을 정할 때 참고하세요.">
                        <FgHeatmap heatmap={data.heatmap} />
                    </Section>

                    <Section
                        title="방문자 환경"
                        description={data.trackedSince
                            ? `${moment(data.trackedSince).format('M월 D일')}부터 수집된 방문 ${formatNumber(data.trackedVisits)}건 기준`
                            : '기기·앱·캠페인 정보는 새로 수집을 시작해서, 쌓이는 대로 표시됩니다.'}
                    >
                        {data.devices ? (
                        <div className="grid grid-cols-1 min-[600px]:grid-cols-3 gap-6">
                            <div>
                                <p className="mb-2 text-xs font-semibold text-gray-500">기기</p>
                                <FgRankBars total={data.trackedVisits} rows={data.devices.map(r => ({ key: r.key, label: DEVICE_LABELS[r.key] || r.key || '알 수 없음', value: r.visits }))} />
                            </div>
                            <div>
                                <p className="mb-2 text-xs font-semibold text-gray-500">열린 앱</p>
                                <FgRankBars total={data.trackedVisits} rows={data.inApps.map(r => ({ key: r.key, label: IN_APP_LABELS[r.key] || r.key || '일반 브라우저', value: r.visits }))} />
                            </div>
                            <div>
                                <p className="mb-2 text-xs font-semibold text-gray-500" title="페이지 주소 뒤에 붙인 ?src= 또는 utm_source">캠페인 (?src=)</p>
                                <FgRankBars total={data.trackedVisits} rows={data.sources.map(r => ({ key: r.key, label: r.key ?? '지정 안 함', value: r.visits }))} />
                            </div>
                        </div>
                        ) : (
                            <ProUpsell title="방문자 환경은 Pro 에서 볼 수 있어요" description="방문자가 쓴 기기, 들어온 앱(인스타·카톡 등), 캠페인(?src=)별 방문을 비교해 보세요." />
                        )}
                        <p className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-500">
                            인스타 바이오에는 <code>shool.uk/내주소?src=insta_bio</code>, 카톡 프로필에는 <code>?src=kakao</code> 처럼 붙여 두면 채널별 성과를 정확히 비교할 수 있어요.
                        </p>
                    </Section>

                    <Section title="캠페인 (UTM)" description="소스(?src= 또는 utm_source) · 매체(utm_medium) · 캠페인(utm_campaign) 조합별 방문과, 그 방문자가 누른 링크 클릭">
                        {data.utm ? (
                            <FgUtmTable rows={data.utm} />
                        ) : (
                            <ProUpsell title="캠페인(UTM) 분석은 Pro 에서 볼 수 있어요" description="인스타 광고·카톡 공지·이벤트처럼 캠페인별로 방문과 링크 클릭을 비교해 보세요." />
                        )}
                        <div className="mt-4 flex flex-col gap-2 rounded-xl bg-gray-50 px-4 py-3 min-[600px]:flex-row min-[600px]:items-center min-[600px]:justify-between">
                            <p className="text-xs text-gray-600 break-keep">유튜브·인스타·카톡에 붙일 <b>채널별 링크</b>를 만들면 여기서 채널끼리 비교할 수 있어요.</p>
                            <button type="button" onClick={goChannelLinks} className="shrink-0 self-start rounded-lg bg-gray-900 px-3 py-2 text-xs font-bold text-white hover:bg-gray-700 min-[600px]:self-auto">채널 링크 만들기 →</button>
                        </div>
                    </Section>

                    <FgLinkDetail open={!!selectedLink} onClose={() => setSelectedLink(null)} userPageId={userPageId} link={selectedLink} range={range} isPro={isPro} />

                    <p className="px-1 text-[11px] text-gray-400">
                        검색 봇·링크 미리보기로 판별된 방문과 클릭은 제외합니다 (기기 정보를 수집하기 시작한 뒤의 기록부터 판별). 유효 방문은 같은 날 같은 브라우저의 재방문을 제외한 수입니다.
                    </p>
                </>
            }
        </div>
    );
}
